import React from 'react';
import { connect } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ToggleOnRoundedIcon from '@mui/icons-material/ToggleOnRounded';
import Swal from "sweetalert2";
import { setProjectDetail } from "../../store/modules/appState/actions";
import { fmtDate, StatusChip, DetailRow, StyledTextarea } from './Helpers';
import FinancialHandler from '../../handlers/FinancialHandler';
import { handleActivateProject } from './HandleBuildProject';

const ProjectDetails = ({
    appState,
    isActive,
    company,
    project,
    setProject,
    projectChange,
    loading,
    setLoading,
    dispatchHandleActivateProject,
    dispatchSetProjectDetail
  }) => {

  const saveProjectSettings = async () => {
    setLoading(true);
    const state = {
      appState: { ...appState, projectDetail: project }
    }
    const projectDetail = await FinancialHandler.saveProjectData(state, "PARTIAL");
    dispatchSetProjectDetail(projectDetail);
    Swal.fire({
      icon: "success",
      title: "Success",
      text: "Your project setting is saved successfully!"
    });
    setLoading(false);
  };

  const activeProject = async () => {
    setLoading(true);
    await dispatchHandleActivateProject(project.projectId);
    Swal.fire({
      icon: "success",
      title: "Success",
      text: `"${project.projectName}" is activated successfully!`
    });
    setLoading(false);
  }

  const deleteProject = async () => {
    setLoading(true);
    await FinancialHandler.deleteProjectData({ projectId: project.projectId });
    await projectChange(appState.projectDetail.projectId, company.id);
    Swal.fire({
      icon: "success",
      title: "Success",
      text: `"${project.projectName}" is deleted successfully!`
    });
    setLoading(false);
  };
  
  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <CircularProgress />
    </Box>
  );
    
  return (
    <Card sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        avatar={<InfoOutlinedIcon color="action" />}
        title="Project Detail"
        titleTypographyProps={{ variant: 'h6' }}
        action={project && 
          <StatusChip value={isActive ? "Active" : "Inactive"}/>}
        className="mr-10"
      />
      <Divider />
      <CardContent sx={{ flex: 1, overflow: 'auto' }}>
        {!project ? (
          <Typography color="text.secondary" className="pt-4 px-10">
            There is no project for this company. Please add a new project to continue.
          </Typography>
        ) : (
          <>
            <Box className="mt-4 mb-10 px-10">
              {/* Editable fields */}
              <FormControl fullWidth required>
                <FormLabel>Project Name</FormLabel>
                <StyledTextarea
                  minRows={1}
                  value={project.projectName}
                  onChange={(e) => setProject({ ...project, projectName: e.target.value })}
                  aria-label="Project Name"
                  placeholder="Enter project name"
                  required
                />
              </FormControl>
              <div className="mb-3" />
              <FormControl fullWidth>
                <FormLabel>Project Description</FormLabel>
                <StyledTextarea
                  minRows={3}
                  value={project.description}
                  onChange={(e) => setProject({ ...project, description: e.target.value })}
                  aria-label="Project Description"
                  placeholder="Enter project description"
                />
              </FormControl>
            </Box>
            
            <Grid container spacing={12} className="mb-10 px-10">
              <Grid item xs={12} md={6}>
                <DetailRow label="Created Date" value={fmtDate(project.createdAt)} />
                <DetailRow label="Updated Date" value={fmtDate(project.updatedAt)} />
              </Grid>
                <Grid item xs={12} md={6}>
                <DetailRow label="Project Start Date" value={fmtDate(project.startAt)} />
                <DetailRow label="Project End Date" value={fmtDate(project.endAt)} />
              </Grid>
            </Grid>

              {/* Actions */}
            <Grid container spacing={12} className="px-10">
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant={project.status === 'Active' ? 'contained' : 'outlined'}
                    color="success"
                    startIcon={<ToggleOnRoundedIcon />}
                    onClick={activeProject}
                    disabled={isActive}
                  >
                    Activate
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={saveProjectSettings}
                    disabled={!isActive || project.projectName.trim().length < 1}
                  >
                    Save Setting
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    onClick={deleteProject}
                    disabled={isActive}
                  >
                    Delete
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const mapDispatchToProps = (dispatch) => ({
  async dispatchHandleActivateProject(projectId) {
    await dispatch(handleActivateProject(projectId));
  },

  async dispatchSetProjectDetail(projectDetail) {
    await dispatch(setProjectDetail(projectDetail));
  },
});

export default connect(null, mapDispatchToProps)(ProjectDetails);
